import { PROOF_LABELS, STATUS_LABELS, STATUS_VALUES, isAnomaly } from "../../lib/procedures";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { AlertTriangle, Camera, Paperclip } from "lucide-react";

// One item of a checklist, as the person on site answers it. Used by the editor's preview and by the execution page.
// Phone first: big touch targets, the anomaly said in words and not only in colour. `fileSlot` lets the execution page put its
// upload control in place of the preview's disabled button.
export default function ChecklistItemField({ item, value, onChange, onBlur, disabled = false, fileSlot = null, testid, maxLength = 2000, rows = 3 }) {
  const anomaly = isAnomaly(item, value);
  const id = `field-${item.id}`;
  const tid = testid || `checklist-item-${item.id}`;

  return (
    <div className="space-y-2" data-testid={tid}>
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-slate-900">
          {item.label}{item.required && <span className="text-red-600" aria-label="obligatoire"> *</span>}
        </label>
        {item.help && <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-line">{item.help}</p>}
      </div>

      {item.kind === "status" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" aria-label={item.label}>
          {STATUS_VALUES.map((v) => {
            const selected = value === v;
            const warn = (item.anomaly_values || []).includes(v);
            return (
              <button key={v} type="button" role="radio" aria-checked={selected} disabled={disabled} onClick={() => onChange(selected ? null : v)}
                data-testid={`${tid}-${v}`}
                className={`h-11 rounded-lg border text-sm font-medium transition-colors disabled:opacity-60 ${selected
                  ? (warn ? "border-amber-500 bg-amber-50 text-amber-900" : v === "ok" ? "border-emerald-600 bg-emerald-50 text-emerald-900" : "border-jamora bg-jamora-50 text-jamora")
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                {STATUS_LABELS[v]}
              </button>
            );
          })}
        </div>
      )}

      {item.kind === "text" && (
        <Textarea id={id} rows={rows} maxLength={maxLength} className="text-base md:text-sm" value={value || ""} disabled={disabled}
          onChange={(e) => onChange(e.target.value)} onBlur={onBlur} data-testid={`${tid}-input`} />
      )}

      {item.kind === "number" && (
        <div className="flex items-center gap-2">
          <Input id={id} type="number" inputMode="decimal" step="any" className="text-base md:text-sm max-w-[12rem]" value={value ?? ""} disabled={disabled}
            onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)} onBlur={onBlur} data-testid={`${tid}-input`} />
          {item.unit && <span className="text-sm text-slate-600">{item.unit}</span>}
          {(item.min !== null && item.min !== undefined) || (item.max !== null && item.max !== undefined) ? (
            <span className="text-xs text-slate-500">attendu : {item.min ?? "…"} – {item.max ?? "…"}{item.unit ? ` ${item.unit}` : ""}</span>
          ) : null}
        </div>
      )}

      {item.kind === "date" && (
        <Input id={id} type="date" className="text-base md:text-sm max-w-[12rem]" value={value || ""} disabled={disabled}
          onChange={(e) => onChange(e.target.value || null)} data-testid={`${tid}-input`} />
      )}

      {item.kind === "choice" && (
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={item.label}>
          {/* Keyed by position: while an option is being typed in the editor, two may be equal (or empty) for a moment. */}
          {item.options.map((option, index) => ({ option, index })).filter(({ option }) => option).map(({ option, index }) => {
            const selected = value === option;
            const warn = (item.anomaly_values || []).includes(option);
            return (
              <button key={index} type="button" role="radio" aria-checked={selected} disabled={disabled} onClick={() => onChange(selected ? null : option)}
                data-testid={`${tid}-option-${option}`}
                className={`min-h-11 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${selected
                  ? (warn ? "border-amber-500 bg-amber-50 text-amber-900" : "border-jamora bg-jamora-50 text-jamora")
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                {option}
              </button>
            );
          })}
        </div>
      )}

      {(item.kind === "photo" || item.kind === "file" || item.proof !== "none") && (
        fileSlot || (
          <Button type="button" variant="outline" disabled className="h-11" data-testid={`${tid}-file`}>
            {item.kind === "photo" || item.proof === "photo" ? <Camera size={16} className="mr-2" /> : <Paperclip size={16} className="mr-2" />}
            {item.kind === "photo" ? "Prendre une photo" : item.kind === "file" ? "Joindre un fichier" : PROOF_LABELS[item.proof]}
          </Button>
        )
      )}

      {anomaly && (
        <p className="flex items-center gap-1.5 text-sm text-amber-800" role="status" data-testid={`${tid}-anomaly`}>
          <AlertTriangle size={14} /> Anomalie : à signaler et à suivre.
        </p>
      )}
    </div>
  );
}
